import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Award,
  Calendar,
  Filter,
  Loader2,
  Mail,
  MessageCircle,
  Target,
  Trophy,
  Users,
  UserRound,
} from "lucide-react";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { cn } from "@/lib/utils";
import {
  api,
  type AudienceGender,
  type AudienceMode,
  type LevelRange,
} from "@/lib/api";

export type AudienceSelection = {
  audience: AudienceMode;
  min_tournaments: number;
  since: string;
  level_ranges: LevelRange[];
  tournament_name_contains: string[];
  gender: AudienceGender;
};

export const defaultAudience = (): AudienceSelection => ({
  audience: "all",
  min_tournaments: 5,
  since: "",
  level_ranges: [],
  tournament_name_contains: [],
  gender: "",
});

type Props = {
  channel: "email" | "whatsapp";
  value: AudienceSelection;
  onChange: (s: AudienceSelection) => void;
  /** Se true, nasconde il pannello preview interno (email/wa contactable).
   * Usato dal Messaging Hub che mostra un funnel autoritativo proprio. */
  hidePreview?: boolean;
};

const AUDIENCE_OPTIONS: Array<{
  id: AudienceMode;
  label: string;
  description: string;
  icon: React.ReactNode;
}> = [
  {
    id: "all",
    label: "Tutti i clienti",
    description: "Invia a tutto il database",
    icon: <Users className="h-4 w-4" />,
  },
  {
    id: "top_fans",
    label: "Top fan",
    description: "≥ N tornei negli ultimi 365gg",
    icon: <Target className="h-4 w-4" />,
  },
  {
    id: "active_since",
    label: "Attivi da data",
    description: "Chi ha giocato dal…",
    icon: <Calendar className="h-4 w-4" />,
  },
];

// Preset richiesti dall'utente: range larghi (3) + range stretti (5)
const LEVEL_PRESETS: Array<{ label: string; range: LevelRange }> = [
  { label: "0–1,5", range: { min: 0, max: 1.5 } },
  { label: "1,5–3", range: { min: 1.5, max: 3 } },
  { label: "3–5", range: { min: 3, max: 5 } },
  { label: "0–1", range: { min: 0, max: 1 } },
  { label: "1–2", range: { min: 1, max: 2 } },
  { label: "2–3", range: { min: 2, max: 3 } },
  { label: "3–4", range: { min: 3, max: 4 } },
  { label: "4–5", range: { min: 4, max: 5 } },
];

// Preset tornei Moving Padel
const TOURNAMENT_PRESETS: string[] = [
  "MOVING SMASH & LUNCH",
  "GIRLS IN PADEL",
  "GIRO A 8 ALLE 18",
];

const GENDER_OPTIONS: Array<{ id: AudienceGender; label: string }> = [
  { id: "", label: "Tutti" },
  { id: "female", label: "Solo donne" },
  { id: "male", label: "Solo uomini" },
];

function rangeKey(r: LevelRange): string {
  return `${r.min}-${r.max}`;
}

export function AudienceSelector({ channel, value, onChange, hidePreview }: Props) {
  const preview = useQuery({
    queryKey: ["audience-preview", value],
    queryFn: () => api.previewAudience(value),
    staleTime: 20_000,
    enabled: !hidePreview,
  });

  const count =
    channel === "email"
      ? preview.data?.email_contactable
      : preview.data?.whatsapp_contactable;

  const toggleLevel = (r: LevelRange) => {
    const k = rangeKey(r);
    const exists = value.level_ranges.some((x) => rangeKey(x) === k);
    onChange({
      ...value,
      level_ranges: exists
        ? value.level_ranges.filter((x) => rangeKey(x) !== k)
        : [...value.level_ranges, r],
    });
  };

  const toggleTournament = (name: string) => {
    const exists = value.tournament_name_contains.includes(name);
    onChange({
      ...value,
      tournament_name_contains: exists
        ? value.tournament_name_contains.filter((n) => n !== name)
        : [...value.tournament_name_contains, name],
    });
  };

  const hasLevelFilter = value.level_ranges.length > 0;
  const hasTournamentFilter = value.tournament_name_contains.length > 0;
  const hasGenderFilter = Boolean(value.gender);

  return (
    <div className="space-y-4">
      {/* ── Audience base ────────────────────────────────────── */}
      <div>
        <div className="mb-2 text-xs font-semibold uppercase text-slate-500">
          Chi ricive il messaggio
        </div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          {AUDIENCE_OPTIONS.map((opt) => {
            const active = value.audience === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => onChange({ ...value, audience: opt.id })}
                className={cn(
                  "rounded-md border p-3 text-left transition",
                  active
                    ? "border-moving-500 bg-moving-50 ring-1 ring-moving-500"
                    : "border-slate-200 bg-white hover:border-slate-300",
                )}
              >
                <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
                  {opt.icon}
                  {opt.label}
                </div>
                <div className="mt-0.5 text-xs text-slate-500">
                  {opt.description}
                </div>
              </button>
            );
          })}
        </div>
        {value.audience === "top_fans" ? (
          <div className="mt-2 flex items-center gap-2 rounded-md bg-slate-50 p-2">
            <Label className="text-xs whitespace-nowrap">
              Minimo tornei giocati
            </Label>
            <Input
              type="number"
              min={1}
              max={50}
              value={value.min_tournaments}
              onChange={(e) =>
                onChange({
                  ...value,
                  min_tournaments: parseInt(e.target.value) || 1,
                })
              }
              className="w-20"
            />
          </div>
        ) : null}
        {value.audience === "active_since" ? (
          <div className="mt-2 flex items-center gap-2 rounded-md bg-slate-50 p-2">
            <Label className="text-xs whitespace-nowrap">Attivi dal</Label>
            <Input
              type="date"
              value={value.since}
              onChange={(e) => onChange({ ...value, since: e.target.value })}
              className="w-44"
            />
          </div>
        ) : null}
      </div>

      {/* ── Filtri aggiuntivi ─────────────────────────────────── */}
      <div className="rounded-md border border-slate-200 bg-white p-3">
        <div className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase text-slate-500">
          <Filter className="h-3 w-3" />
          Filtri aggiuntivi (combinati con AND)
        </div>

        {/* Genere */}
        <div className="mb-3">
          <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase text-slate-500">
            <UserRound className="h-3 w-3" />
            Genere
          </div>
          <div className="flex flex-wrap gap-1.5">
            {GENDER_OPTIONS.map((g) => {
              const active = value.gender === g.id;
              return (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => onChange({ ...value, gender: g.id })}
                  className={cn(
                    "rounded-full border px-2.5 py-0.5 text-xs font-medium transition",
                    active
                      ? "border-court-500 bg-court-500 text-white"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300",
                  )}
                >
                  {g.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Livelli */}
        <div className="mb-3">
          <div className="mb-1 flex items-center justify-between">
            <div className="flex items-center gap-1 text-[11px] font-semibold uppercase text-slate-500">
              <Award className="h-3 w-3" />
              Fasce di livello (OR tra chip selezionate)
            </div>
            {hasLevelFilter ? (
              <button
                type="button"
                onClick={() => onChange({ ...value, level_ranges: [] })}
                className="text-[10px] text-slate-500 hover:text-red-600"
              >
                ✕ deseleziona
              </button>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {LEVEL_PRESETS.map((p) => {
              const active = value.level_ranges.some(
                (r) => rangeKey(r) === rangeKey(p.range),
              );
              return (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => toggleLevel(p.range)}
                  className={cn(
                    "rounded-full border px-2.5 py-0.5 text-xs font-medium transition",
                    active
                      ? "border-moving-500 bg-moving-500 text-white"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300",
                  )}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tornei */}
        <div>
          <div className="mb-1 flex items-center justify-between">
            <div className="flex items-center gap-1 text-[11px] font-semibold uppercase text-slate-500">
              <Trophy className="h-3 w-3" />
              Hanno partecipato a (OR tra tornei)
            </div>
            {hasTournamentFilter ? (
              <button
                type="button"
                onClick={() =>
                  onChange({ ...value, tournament_name_contains: [] })
                }
                className="text-[10px] text-slate-500 hover:text-red-600"
              >
                ✕ deseleziona
              </button>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {TOURNAMENT_PRESETS.map((name) => {
              const active = value.tournament_name_contains.includes(name);
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => toggleTournament(name)}
                  className={cn(
                    "rounded-full border px-2.5 py-0.5 text-xs font-medium transition",
                    active
                      ? "border-court-500 bg-court-500 text-white"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300",
                  )}
                >
                  {name}
                </button>
              );
            })}
          </div>
          {hasTournamentFilter && value.audience === "all" ? (
            <div className="mt-1 text-[10px] text-slate-500 italic">
              Con questo filtro attivo il pool si limita ai giocatori presenti
              nella cache storico (non più "tutti i clienti").
            </div>
          ) : null}
        </div>
      </div>

      {/* ── Preview count ───────────────────────────────────── */}
      {!hidePreview ? (
      <div
        className={cn(
          "rounded-md border p-3 text-sm",
          preview.isError
            ? "border-red-200 bg-red-50 text-red-800"
            : "border-court-200 bg-court-50 text-court-900",
        )}
      >
        {preview.isLoading ? (
          <div className="flex items-center gap-2 text-court-700">
            <Loader2 className="h-4 w-4 animate-spin" />
            Calcolo destinatari…
          </div>
        ) : preview.isError ? (
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <div className="font-medium">Preview non disponibile</div>
              <div className="text-xs mt-0.5">
                {String(
                  (preview.error as { body?: { detail?: string } })?.body
                    ?.detail ?? preview.error,
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <div className="flex items-center gap-1.5 font-semibold text-lg">
              {channel === "email" ? (
                <Mail className="h-4 w-4" />
              ) : (
                <MessageCircle className="h-4 w-4" />
              )}
              {count ?? 0}
              <span className="text-xs font-normal text-court-700">
                destinatari {channel === "email" ? "email" : "WhatsApp"}
              </span>
            </div>
            <div className="text-xs text-court-700">
              · pool: {preview.data?.selected_clients ?? 0}
            </div>
            {(hasLevelFilter || hasTournamentFilter || hasGenderFilter) ? (
              <div className="text-xs text-court-700">· filtri attivi</div>
            ) : null}
          </div>
        )}
      </div>
      ) : null}
    </div>
  );
}
